import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  InputAdornment,
  Autocomplete,
  Chip,
} from '@mui/material';
import Iconify from '../iconify';
import * as adminOrgService from '../../services/adminOrganizationService';
import * as adminService from '../../services/adminService';
import { errorNotification, infoNotification } from '../../services/notificationService';
import ManageMembersDialog from './ManageMembersDialog';

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString();
};

export default function AdminOrganizationsTab() {
  // List state
  const [organizations, setOrganizations] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');

  // Action menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createOwner, setCreateOwner] = useState(null);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerOptions, setOwnerOptions] = useState([]);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const [orgDetails, setOrgDetails] = useState(null);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');

  // Manage members dialog state
  const [membersOpen, setMembersOpen] = useState(false);

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    try {
      const data = await adminOrgService.listOrganizations(
        searchDebounced || null,
        page,
        rowsPerPage
      );
      setOrganizations(data.organizations);
      setTotalElements(data.totalElements);
    } catch (error) {
      errorNotification('Failed to load organizations');
    }
  }, [searchDebounced, page, rowsPerPage]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load owner autocomplete options
  useEffect(() => {
    if (!ownerSearch) {
      setOwnerOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await adminService.listUsers(ownerSearch);
        const available = (data.users || []).filter((u) => !u.organizationId);
        setOwnerOptions(available);
      } catch {
        setOwnerOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [ownerSearch]);

  // Handlers
  const handleOpenMenu = (event, org) => {
    setAnchorEl(event.currentTarget);
    setSelectedOrg(org);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedOrg(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // Create organization
  const handleCreate = async () => {
    if (!createName || !createOwner) return;
    try {
      await adminOrgService.createOrganization(createName, createDescription, createOwner.id);
      infoNotification('Organization created successfully');
      setCreateOpen(false);
      setCreateName('');
      setCreateDescription('');
      setCreateOwner(null);
      setOwnerSearch('');
      fetchOrganizations();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to create organization');
    }
  };

  // Edit organization
  const handleOpenEdit = () => {
    setEditName(selectedOrg.name);
    setEditDescription(selectedOrg.description || '');
    setEditOpen(true);
    setAnchorEl(null);
  };

  const handleEdit = async () => {
    if (!editName) return;
    try {
      await adminOrgService.updateOrganization(selectedOrg.id, editName, editDescription);
      infoNotification('Organization updated');
      setEditOpen(false);
      fetchOrganizations();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to update organization');
    }
  };

  // Transfer ownership
  const handleOpenTransfer = async () => {
    setAnchorEl(null);
    try {
      const details = await adminOrgService.getOrganization(selectedOrg.id);
      setOrgDetails(details);
      setTransferTarget(null);
      setTransferOpen(true);
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to load organization details');
    }
  };

  const handleTransfer = async () => {
    if (!transferTarget) return;
    try {
      await adminOrgService.transferOwnership(selectedOrg.id, transferTarget.id);
      infoNotification('Ownership transferred');
      setTransferOpen(false);
      fetchOrganizations();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to transfer ownership');
    }
  };

  // Delete organization
  const handleOpenDelete = () => {
    setDeleteConfirmName('');
    setDeleteOpen(true);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedOrg || deleteConfirmName !== selectedOrg.name) return;
    try {
      await adminOrgService.deleteOrganization(selectedOrg.id);
      infoNotification('Organization deleted');
      setDeleteOpen(false);
      setDeleteConfirmName('');
      fetchOrganizations();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to delete organization');
    }
  };

  // Manage members
  const handleOpenMembers = () => {
    setMembersOpen(true);
    setAnchorEl(null);
  };

  const transferCandidates = (orgDetails?.users || []).filter(
    (u) => u.role !== 'owner' && u.role !== 'viewer'
  );

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" spacing={1} mb={2}>
        <Button
          variant="outlined"
          startIcon={<Iconify icon="eva:refresh-fill" />}
          onClick={fetchOrganizations}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<Iconify icon="eva:plus-fill" />}
          onClick={() => setCreateOpen(true)}
        >
          Create Organization
        </Button>
      </Stack>

      <Card>
        <Stack sx={{ p: 2 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search organizations..."
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 320 }}
          />
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{org.name}</Typography>
                  </TableCell>
                  <TableCell>{org.ownerName || '\u2014'}</TableCell>
                  <TableCell>
                    {org.subscriptionTier ? (
                      <Chip
                        label={org.subscriptionTier.charAt(0).toUpperCase() + org.subscriptionTier.slice(1)}
                        color={
                          { beta: 'info', bronze: 'warning', silver: 'default', gold: 'success' }[
                            org.subscriptionTier
                          ] || 'default'
                        }
                        size="small"
                      />
                    ) : (
                      <Chip label="None" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{org.memberCount ?? 0}</TableCell>
                  <TableCell>{formatDate(org.createdAt)}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={(e) => handleOpenMenu(e, org)}>
                      <Iconify icon="eva:more-vertical-fill" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {organizations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No organizations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleOpenEdit}>Edit Details</MenuItem>
        <MenuItem onClick={handleOpenMembers}>Manage Members</MenuItem>
        <MenuItem onClick={handleOpenTransfer}>Transfer Ownership</MenuItem>
        <MenuItem sx={{ color: 'error.main' }} onClick={handleOpenDelete}>
          Delete
        </MenuItem>
      </Menu>

      {/* Create Organization Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Organization</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Description"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 200 }}
            />
            <Autocomplete
              options={ownerOptions}
              getOptionLabel={(option) => `${option.username} (${option.email})`}
              value={createOwner}
              onChange={(e, value) => setCreateOwner(value)}
              onInputChange={(e, value) => setOwnerSearch(value)}
              renderInput={(params) => <TextField {...params} label="Owner" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={ownerSearch ? 'No users found' : 'Type to search users'}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!createName || !createOwner}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              autoFocus
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 200 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit} disabled={!editName}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Ownership</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              Select a member to transfer ownership of <strong>{selectedOrg?.name}</strong>.
            </DialogContentText>
            <Autocomplete
              options={transferCandidates}
              getOptionLabel={(option) => `${option.username} (${option.email})`}
              value={transferTarget}
              onChange={(e, value) => setTransferTarget(value)}
              renderInput={(params) => <TextField {...params} label="New Owner" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No eligible members"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleTransfer} disabled={!transferTarget}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Organization Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DialogContentText>
              This action cannot be undone. To confirm, type the organization name{' '}
              <strong>{selectedOrg?.name}</strong> below.
            </DialogContentText>
            <TextField
              label="Organization Name"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteConfirmName !== selectedOrg?.name}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Members Dialog */}
      <ManageMembersDialog
        open={membersOpen}
        onClose={() => {
          setMembersOpen(false);
          fetchOrganizations();
        }}
        organization={selectedOrg}
      />
    </>
  );
}
